<script setup lang="ts">
import { useNotificationsStore } from '@/stores/notifications'
import { useRouter } from 'vue-router'

const emit = defineEmits<{ close: [] }>()

const notifications = useNotificationsStore()
const router = useRouter()

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

async function handleNotificationClick(id: string, link?: string | null) {
  await notifications.markRead(id)
  if (link) {
    router.push(link)
  }
  emit('close')
}
</script>

<template>
  <div class="notification-panel">
    <div class="panel-header">
      <span class="panel-title">Notifications</span>
      <button
        v-if="notifications.unreadCount > 0"
        class="mark-all-btn"
        type="button"
        @click="notifications.markAllRead()"
      >
        Mark all read
      </button>
      <button
        autofocus
        class="close-btn"
        type="button"
        aria-label="Close notifications"
        @click="emit('close')"
      >
        <i class="pi pi-times" aria-hidden="true" />
      </button>
    </div>

    <ul v-if="notifications.notifications.length > 0" class="notification-list">
      <li
        v-for="n in notifications.notifications.slice(0, 20)"
        :key="n.id"
        class="notification-item"
        :class="{ unread: n.read_at == null }"
        role="button"
        tabindex="0"
        @click="handleNotificationClick(n.id, n.link)"
        @keydown.enter="handleNotificationClick(n.id, n.link)"
        @keydown.space.prevent="handleNotificationClick(n.id, n.link)"
      >
        <span v-if="n.read_at == null" class="unread-dot" aria-label="Unread" />
        <div class="notification-body">
          <span class="notification-message">{{ n.message }}</span>
          <span class="notification-time">{{ formatRelativeTime(n.created_at) }}</span>
        </div>
      </li>
    </ul>

    <p v-else class="empty-state">No notifications yet</p>
  </div>
</template>

<style scoped>
.notification-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: var(--bg-card);
  border-left: var(--space-px) solid var(--border);
}

.panel-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-bottom: var(--space-px) solid var(--border);
  position: sticky;
  top: 0;
  background: var(--bg-card);
  z-index: 1;
}

.panel-title {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
}

.mark-all-btn {
  font-size: var(--font-size-xs);
  color: var(--green-primary);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.mark-all-btn:hover {
  text-decoration: underline;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--space-6);
  height: var(--space-6);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  padding: 0;
}

.close-btn:hover {
  background: var(--bg-card-hover);
  color: var(--text-primary);
}

.close-btn i {
  font-size: var(--font-size-xs);
}

.notification-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  cursor: pointer;
  border-bottom: var(--space-px) solid var(--border);
  transition: background 0.12s;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item:hover {
  background: var(--bg-card-hover);
}

.notification-item:focus-visible {
  outline: var(--focus-ring-width) solid var(--green-primary);
  outline-offset: var(--focus-ring-inset);
}

.unread-dot {
  width: var(--space-2);
  height: var(--space-2);
  border-radius: 50%;
  background: var(--color-unseen-dot);
  flex-shrink: 0;
  margin-top: var(--space-1_5);
}

.notification-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.notification-message {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  line-height: 1.4;
}

.notification-time {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.empty-state {
  padding: var(--space-6) var(--space-4);
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}
</style>
